import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, Globe, Zap, CreditCard, Smartphone, CheckCircle, Link, Building2, Layers } from 'lucide-react';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { useUser } from '../context/UserContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.modanmic.finance&pcampaignid=web_share';

  // "Open Account" / "Sign In" / "Get Started" should send an already
  // logged-in user straight to their dashboard instead of back to the
  // login screen.
  const goAuth = () => navigate(user ? '/dashboard' : '/login');

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="text-xl font-semibold tracking-tight text-slate-900 cursor-pointer" onClick={() => navigate('/')}>MODANMIC</div>
            <div className="flex items-center space-x-3">
              {user ? (
                <>
                  <button onClick={() => navigate('/dashboard')} className="px-5 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {user.telegramName?.charAt(1)?.toUpperCase() || 'U'}
                    </div>
                    <span className="hidden sm:inline">{user.telegramName}</span>
                  </button>
                  <button onClick={() => navigate('/dashboard')} className="px-5 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-all shadow-sm">Dashboard</button>
                </>
              ) : (
                <>
                  <button onClick={goAuth} className="px-5 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors">Sign In</button>
                  <button onClick={goAuth} className="px-5 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-all shadow-sm">Open Account</button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-3.5 py-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-blue-900">Trusted by 100,000+ users globally</span>
                </div>
              </div>
              <h1 className="text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight tracking-tight">The best bank to manage your finance</h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">Experience banking designed for the modern world. Send, receive, and manage your money across borders with enterprise-grade security and zero complexity.</p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button onClick={goAuth} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-all shadow-sm group">
                  Open Account <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button onClick={goAuth} className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-slate-300 text-slate-900 text-sm font-medium rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-all">Sign In</button>
              </div>
              <div className="grid grid-cols-3 gap-8 pt-8">
                <div><div className="text-3xl font-semibold text-slate-900">2M+</div><div className="text-sm text-slate-600 mt-1">Active users</div></div>
                <div><div className="text-3xl font-semibold text-slate-900">99.9%</div><div className="text-sm text-slate-600 mt-1">Uptime SLA</div></div>
                <div><div className="text-3xl font-semibold text-slate-900">24/7</div><div className="text-sm text-slate-600 mt-1">Support</div></div>
              </div>
            </div>
            <div className="relative flex items-center justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/40 to-purple-100/40 blur-3xl rounded-full opacity-60"></div>
                <div className="relative w-[300px] h-[600px] bg-slate-900 rounded-[2.5rem] p-2.5 shadow-2xl">
                  <div className="w-full h-full bg-white rounded-[2rem] overflow-hidden">
                    <div className="h-full bg-slate-50 p-6">
                      <div className="text-xs text-slate-600 mb-1">Good morning,</div>
                      <div className="text-xl font-semibold text-slate-900 mb-6">Welcome</div>
                      <div className="p-5 bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl shadow-lg mb-6">
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Available Balance</p>
                        <p className="text-3xl font-semibold text-white mt-2">$50,000.00</p>
                        <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-600/50">
                          <span className="text-sm text-slate-300">•••• 4729</span>
                          <span className="text-xs text-slate-400 font-medium">USD ACCOUNT</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <button className="bg-blue-50 text-blue-600 rounded-xl p-3 text-sm font-medium">Send</button>
                        <button className="bg-green-50 text-green-600 rounded-xl p-3 text-sm font-medium">Receive</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stripe Integration Section ── */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-indigo-950 via-slate-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 30% 50%,rgba(99,102,241,.4) 0%,transparent 60%),radial-gradient(circle at 70% 50%,rgba(139,92,246,.3) 0%,transparent 60%)'}}></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-400/20 rounded-full px-3.5 py-1.5">
                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                <span className="text-xs font-medium text-indigo-300">Powered by Stripe</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-semibold text-white leading-tight tracking-tight">
                MODANMIC is integrated with <span className="text-indigo-400">Stripe</span>
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                We've partnered with Stripe — the world's leading payments infrastructure — to deliver seamless, secure, and compliant payment processing for every transaction on our platform.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {title:'Instant Payouts',desc:'Receive funds directly to your connected bank account in real time.'},
                  {title:'PCI-DSS Compliant',desc:"Your card data is handled by Stripe's certified secure infrastructure."},
                  {title:'Multi-Currency',desc:'Accept and send payments in USD, GBP, EUR and 130+ currencies.'},
                  {title:'Fraud Prevention',desc:'Stripe Radar detects and blocks suspicious activity automatically.'},
                ].map((item,i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={16} className="text-indigo-400 flex-shrink-0" />
                      <span className="text-white font-semibold text-sm">{item.title}</span>
                    </div>
                    <p className="text-slate-400 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-600/20 blur-3xl rounded-full scale-150"></div>
                <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-10 text-center shadow-2xl">
                  <div className="flex items-center justify-center gap-4 mb-8">
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                      <span className="text-indigo-600 font-black text-lg tracking-tight">S</span>
                    </div>
                    <Link size={24} className="text-indigo-400" />
                    <div className="w-16 h-16 bg-slate-900 border border-slate-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-black text-xs tracking-tight">MDM</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {['Payment Processing','Identity Verification','Instant Payouts','Dispute Protection'].map((label,i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <span className="text-slate-400 text-sm">{label}</span>
                        <span className="text-xs font-semibold text-green-400 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block"></span>Active
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Companies Integration Section ── */}
      <section className="py-20 px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-emerald-50 text-emerald-900 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">Enterprise Integration</div>
            <h2 className="text-4xl lg:text-5xl font-semibold text-slate-900 mb-4 tracking-tight">Integrate MODANMIC into your business</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Join leading companies that power their financial operations with MODANMIC's flexible API and enterprise-grade infrastructure.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {[
              {icon:<Building2 size={24} className="text-blue-600"/>,bg:'bg-blue-50',type:'E-Commerce Platforms',desc:'Accept payments, issue refunds, and manage customer wallets all from one unified API. Perfect for marketplaces and online stores.',features:['Embedded checkout','Refund management','Multi-seller payouts']},
              {icon:<Layers size={24} className="text-purple-600"/>,bg:'bg-purple-50',type:'SaaS & Subscriptions',desc:'Automate recurring billing, manage subscription plans, and handle upgrades and downgrades without developer overhead.',features:['Recurring billing','Plan management','Prorated invoicing']},
              {icon:<Globe size={24} className="text-emerald-600"/>,bg:'bg-emerald-50',type:'Remittance Services',desc:'Enable cross-border transfers for your customers at competitive rates with compliance built in from day one.',features:['130+ currencies','FX conversion','Compliance tools']},
              {icon:<Smartphone size={24} className="text-orange-600"/>,bg:'bg-orange-50',type:'Mobile Applications',desc:'Add a full-featured wallet to your app in days — not months. Our SDK supports iOS, Android, and React Native.',features:['Mobile SDK','Push notifications','Biometric auth']},
              {icon:<Shield size={24} className="text-red-600"/>,bg:'bg-red-50',type:'Financial Institutions',desc:'White-label our banking infrastructure for your brand. Offer accounts, cards, and transfers under your own identity.',features:['White-label ready','Core banking API','Regulatory support']},
              {icon:<Zap size={24} className="text-yellow-600"/>,bg:'bg-yellow-50',type:'Crypto & Web3',desc:'Bridge traditional finance with crypto. Support BTC, ETH, and more alongside fiat in a single unified experience.',features:['BTC / ETH / BCH','Wallet custody','Fiat on-ramp']},
            ].map((card,i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-7 shadow-sm hover:shadow-lg transition-all duration-300 group">
                <div className={`w-12 h-12 ${card.bg} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>{card.icon}</div>
                <h3 className="text-base font-bold text-slate-900 mb-3">{card.type}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-5">{card.desc}</p>
                <div className="space-y-2">
                  {card.features.map((f,j) => (
                    <div key={j} className="flex items-center gap-2">
                      <CheckCircle size={13} className="text-emerald-500 flex-shrink-0" />
                      <span className="text-xs text-slate-600 font-medium">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Ready to integrate?</h3>
              <p className="text-slate-400 text-sm max-w-xl">Get API access, sandbox credentials, and dedicated support to bring MODANMIC into your product.</p>
            </div>
            <a href="mailto:modanmic5302023@gmail.com" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-slate-900 font-semibold text-sm rounded-xl hover:bg-slate-100 transition-all whitespace-nowrap flex-shrink-0">
              Contact Sales <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-50 text-blue-900 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">Payment Methods</div>
            <h2 className="text-4xl lg:text-5xl font-semibold text-slate-900 mb-4 tracking-tight">Accept payments everywhere</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Integrate with the world's most trusted payment platforms. Give your customers the flexibility they expect.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {icon:<CreditCard className="text-white" size={24}/>,title:'Apple Pay',description:'Apple Pay enables fast, secure, and contactless payments using Apple devices with biometric authentication.',gradient:'from-slate-900 to-slate-700',badge:'Popular'},
              {icon:<Smartphone className="text-white" size={24}/>,title:'Google Pay',description:'Google Pay enables fast, secure, and contactless payments via smartphones, with easy bank integration.',gradient:'from-blue-600 to-blue-500',badge:'Recommended'},
              {icon:<Zap className="text-white" size={24}/>,title:'ACH Direct Debit',description:'ACH Direct Debit enables automated, secure bank-to-bank payments for recurring bills and invoices.',gradient:'from-purple-600 to-purple-500',badge:'Automated'},
              {icon:<CreditCard className="text-white" size={24}/>,title:'Credit Card',description:'Credit cards provide flexible, secure payment with credit limits, rewards, and worldwide acceptance.',gradient:'from-pink-600 to-rose-500',badge:'Flexible'},
            ].map((method,idx) => (
              <div key={idx} className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 relative overflow-hidden">
                {method.badge && <div className="absolute top-4 right-4"><span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full">{method.badge}</span></div>}
                <div className={`w-14 h-14 bg-gradient-to-br ${method.gradient} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>{method.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{method.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{method.description}</p>
                <button className="text-sm font-semibold text-blue-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Learn More <ArrowRight size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-20 px-6 lg:px-8 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1.5">
                <Smartphone size={18} className="text-green-300" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-green-200">Mobile App</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight">Download the MODANMIC app on Google Play</h2>
              <p className="text-slate-300 text-lg max-w-xl leading-relaxed">Scan the QR code or tap the button to open the MODANMIC mobile app on Google Play. Manage your wallet, transfers, and notifications from your phone.</p>
              <a href={playStoreUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 px-6 py-3 bg-green-500 hover:bg-green-400 rounded-full text-sm font-semibold text-white transition-all shadow-lg">
                <Smartphone size={18} /> Open in Google Play
              </a>
            </div>
            <div className="flex items-center justify-center">
              <div className="rounded-[2.5rem] bg-white p-8 shadow-2xl">
                <QRCode value={playStoreUrl} size={240} level="H" includeMargin />
                <p className="mt-5 text-center text-sm text-slate-600">Scan to download the MODANMIC app from Google Play</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-purple-50 text-purple-900 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">Features</div>
            <h2 className="text-4xl lg:text-5xl font-semibold text-slate-900 mb-4 tracking-tight">Everything you need</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Powerful features to manage your finances with confidence</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {icon:<Zap className="text-white" size={20}/>,title:'Instant Transfers',description:'Send and receive money globally in seconds with zero fees.',gradient:'from-blue-600 to-blue-500'},
              {icon:<Shield className="text-white" size={20}/>,title:'Bank-Grade Security',description:'Enterprise encryption and 24/7 fraud monitoring.',gradient:'from-purple-600 to-purple-500'},
              {icon:<CreditCard className="text-white" size={20}/>,title:'Crypto Integration',description:'Buy, sell, and swap cryptocurrencies seamlessly.',gradient:'from-pink-600 to-rose-500'},
              {icon:<Globe className="text-white" size={20}/>,title:'Global Access',description:'Access your account anywhere, anytime on any device.',gradient:'from-emerald-600 to-emerald-500'},
              {icon:<Smartphone className="text-white" size={20}/>,title:'Mobile First',description:'Designed for modern mobile banking experience.',gradient:'from-orange-600 to-orange-500'},
              {icon:<CheckCircle className="text-white" size={20}/>,title:'Investment Tools',description:'Grow your wealth with our investment platform.',gradient:'from-indigo-600 to-indigo-500'},
            ].map((feature,idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-slate-100">
                <div className={`w-11 h-11 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-5`}>{feature.icon}</div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden bg-slate-900 rounded-2xl p-12 lg:p-16 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-transparent"></div>
            <div className="relative z-10">
              <h2 className="text-4xl lg:text-5xl font-semibold text-white mb-5 tracking-tight">Ready to get started?</h2>
              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">Join thousands of users managing their finances with MODANMIC. Open an account in minutes.</p>
              <button onClick={goAuth} className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-slate-900 text-sm font-medium rounded-lg hover:bg-slate-50 transition-all shadow-lg">
                Get Started Today <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div className="lg:col-span-2">
              <div className="text-xl font-semibold mb-4">MODANMIC</div>
              <p className="text-slate-400 leading-relaxed max-w-md text-sm">Modern digital banking and cross-border finance solutions for users worldwide.</p>
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-4 text-white">Support</h3>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/help" className="text-slate-400 hover:text-white transition-colors">Help Center</a></li>
                <li><a href="mailto:modanmic5302023@gmail.com" className="text-slate-400 hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="/privacy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-4 text-white">Connect</h3>
              <ul className="space-y-2.5 text-sm">
                <li><a href="mailto:modanmic5302023@gmail.com" className="text-slate-400 hover:text-white transition-colors">modanmic5302023@gmail.com</a></li>
                <li><a href="https://www.youtube.com/@modanmicOffical" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">YouTube</a></li>
                <li><a href="https://x.com/modanmicoffical" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">X (Twitter)</a></li>
                <li><button onClick={() => navigate('/certifications')} className="text-blue-400 hover:text-blue-300 transition-colors font-medium flex items-center gap-1"><Shield size={14} />View Certifications</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <p className="text-slate-500 text-center text-sm">© 2026 MODANMIC. All rights reserved. · Payments powered by Stripe.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
